import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Loader2, Pause, Play, Square } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { autopilotApi, type AutopilotRun } from '@/api/autopilotApi'
import { friendlyMessage } from '@/api/http'
import { ProseRenderer } from '@/components/reader/ProseRenderer'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import type { Chapter } from '@/types/nf'

const ADVANCING_STATUSES = new Set<AutopilotRun['status']>(['planning', 'running'])
const DECISION_STATUSES = new Set<AutopilotRun['status']>(['paused', 'blocked_plan', 'blocked_credits'])

export function autopilotRefetchInterval(run: AutopilotRun | null | undefined): number | false {
  return run && ADVANCING_STATUSES.has(run.status) ? 5000 : false
}

export function latestGeneratedChapter(run: AutopilotRun, chapters: Chapter[]): Chapter | undefined {
  return chapters
    .filter((chapter) => chapter.chapter_number <= run.cursor_chapter_no && Boolean(chapter.content?.trim()))
    .sort((left, right) => right.chapter_number - left.chapter_number)[0]
}

interface AutopilotRunBarViewProps {
  run: AutopilotRun
  chapters: Chapter[]
  contentLanguage?: string
  actionPending?: boolean
  onPause: () => void
  onResume: () => void
  onCancel: () => void
  onEditOutline: () => void
}

export function AutopilotRunBarView({
  run,
  chapters,
  contentLanguage,
  actionPending = false,
  onPause,
  onResume,
  onCancel,
  onEditOutline,
}: AutopilotRunBarViewProps) {
  const { t } = useTranslation()
  const latestChapter = latestGeneratedChapter(run, chapters)
  const currentTitle = chapters.find((chapter) => chapter.chapter_number === run.cursor_chapter_no)?.title
    || latestChapter?.title
  const needsDecision = DECISION_STATUSES.has(run.status)
  const quality = run.last_chapter_quality > 0 ? String(run.last_chapter_quality) : '—'

  return (
    <Card className="mb-4 gap-4 border-primary/40 py-4 shadow-none">
      <CardHeader className="gap-3 px-4 sm:px-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0 space-y-1">
            <div className="flex flex-wrap items-center gap-2">
              {ADVANCING_STATUSES.has(run.status) ? <Loader2 className="size-4 animate-spin text-primary" /> : null}
              <CardTitle className="text-base">{t('autopilot.title')}</CardTitle>
              <Badge variant="secondary">{t(`autopilot.status.${run.status}`)}</Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {t('autopilot.progress', {
                current: run.cursor_chapter_no,
                end: run.end_chapter_no,
                title: currentTitle ? ` · ${currentTitle}` : '',
                spent: run.credits_spent,
                estimated: run.credits_estimated,
              })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {ADVANCING_STATUSES.has(run.status) ? (
              <Button variant="outline" size="sm" disabled={actionPending} onClick={onPause}>
                <Pause className="size-4" />
                {t('autopilot.pause')}
              </Button>
            ) : null}
            {needsDecision ? (
              <>
                <Button size="sm" disabled={actionPending} onClick={onResume}>
                  <Play className="size-4" />
                  {t('autopilot.resume')}
                </Button>
                <Button variant="outline" size="sm" disabled={actionPending} onClick={onCancel}>
                  <Square className="size-4" />
                  {t('autopilot.cancel')}
                </Button>
              </>
            ) : null}
          </div>
        </div>
        <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
          <span>{t('autopilot.lastQuality', { score: quality })}</span>
          <span>{t('autopilot.nextCheckpoint', { chapter: run.next_checkpoint_chapter_no })}</span>
        </div>
      </CardHeader>

      {needsDecision ? (
        <CardContent className="space-y-4 border-t px-4 pt-4 sm:px-5">
          <div>
            <p className="font-medium">{t('autopilot.checkpointTitle')}</p>
            <p className="mt-1 text-sm text-muted-foreground">{t(`autopilot.reason.${run.pause_reason || 'user'}`)}</p>
            {run.pause_detail ? (
              <p className="mt-2 whitespace-pre-wrap break-words rounded-md border bg-muted/40 px-3 py-2 font-mono text-xs text-muted-foreground">
                {run.pause_detail}
              </p>
            ) : null}
          </div>
          {latestChapter ? (
            <div className="max-h-80 overflow-y-auto rounded-lg border bg-background/60 p-4">
              <p className="mb-3 text-sm font-medium">
                {t('autopilot.chapterTitle', { chapter: latestChapter.chapter_number, title: latestChapter.title })}
              </p>
              <ProseRenderer content={latestChapter.content} lang={contentLanguage} />
            </div>
          ) : (
            <p className="rounded-lg border bg-muted/30 p-4 text-sm text-muted-foreground">{t('autopilot.noProse')}</p>
          )}
          {(run.pending_entity_reviews?.length ?? 0) > 0 ? <div className="space-y-2">
            <a className="text-sm underline" href="?tab=ingestReview">{t('ingestReview.identity.pendingLink')}</a>
            <ul className="text-sm">{run.pending_entity_reviews?.map((review) => <li key={review.id}>第 {review.chapter_no} 章 · {review.canonical_name}</li>)}</ul>
          </div> : null}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" disabled={actionPending} onClick={onResume}>{t('autopilot.resume')}</Button>
            <Button variant="outline" size="sm" disabled={actionPending} onClick={onEditOutline}>
              {t('autopilot.editOutline')}
            </Button>
            <Button variant="outline" size="sm" disabled={actionPending} onClick={onCancel}>
              {t('autopilot.cancel')}
            </Button>
          </div>
        </CardContent>
      ) : null}
    </Card>
  )
}

interface AutopilotRunBarProps {
  projectId: string
  branchId: string
  chapters: Chapter[]
  contentLanguage?: string
  onEditOutline: () => void
}

export function AutopilotRunBar({
  projectId,
  branchId,
  chapters,
  contentLanguage,
  onEditOutline,
}: AutopilotRunBarProps) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const queryKey = ['autopilot-run', projectId, branchId] as const
  const runQuery = useQuery({
    queryKey,
    queryFn: () => autopilotApi.getActive(projectId, branchId),
    enabled: Boolean(projectId),
    refetchInterval: (query) => autopilotRefetchInterval(query.state.data),
  })

  const actionMutation = useMutation({
    mutationFn: async ({ action, runId }: { action: 'pause' | 'resume' | 'cancel'; runId: string }) => {
      return autopilotApi[action](projectId, runId)
    },
    onSuccess: async (run) => {
      queryClient.setQueryData(queryKey, run)
      await queryClient.invalidateQueries({ queryKey: ['chapters'] })
    },
    onError: (error) => toast.error(friendlyMessage(error, t('autopilot.actionFailed'))),
  })

  const run = runQuery.data
  if (!run) return null

  const mutate = (action: 'pause' | 'resume' | 'cancel') => {
    actionMutation.mutate({ action, runId: run.id })
  }

  return (
    <AutopilotRunBarView
      run={run}
      chapters={chapters}
      contentLanguage={contentLanguage}
      actionPending={actionMutation.isPending}
      onPause={() => mutate('pause')}
      onResume={() => mutate('resume')}
      onCancel={() => mutate('cancel')}
      onEditOutline={onEditOutline}
    />
  )
}
